import React, { useMemo, useState } from "react";

import * as d3 from "d3";
import { useIntl } from "react-intl";

import { Tooltip } from "ds/Tooltip";
import { autoFormatBytes } from "Libs/utils";
import { formatTimeFrame } from "src/organization/pages/project/environment/metrics/components/timeframeControl/util";
import useMetricsContext from "src/organization/pages/project/environment/metrics/hooks/useMetricsContext";
import {
  getDefaultDomain,
  getLinearAxis,
  useCursorDataSync
} from "src/organization/pages/project/environment/metrics/http/helper";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import { getXTickCount } from "src/organization/pages/project/environment/metrics/utils/helper";

import { ChartInfo, HttpLayout } from "../components";
import NoData from "../components/nodata";
import useUpdateDimension from "../hook/useUpdateDimension";
import * as S from "../style";
import { HttpMetricsType } from "../types";

import Area from "./Area";
import BandwidthOptionFilter from "./BandwidthOption";
import { BandwidthOptionWrapper } from "./style";
import TooltipContent from "./Tooltip";
import { BandWidthMetrics, BandWidthSeries } from "./type";
import { bandwidthOption, getBandwidthMetrics } from "./utils";

const chartId = "http-bandwidth";

const getDomains = (data: BandWidthSeries[], timeFrame?: TimeFrameType) => {
  const flatSeries = data.flat();
  const defaultDomain = getDefaultDomain(timeFrame);
  const domains = {
    xDomain: defaultDomain.xDomain,
    yDomain: [0, d3.max(flatSeries, d => Math.max(d[0], d[1]))]
  };
  return flatSeries.length ? domains : defaultDomain;
};

const HttpBandWidth = ({ data }: { data: HttpMetricsType[] }) => {
  const intl = useIntl();
  const { timeFrame } = useMetricsContext();

  const [bandwidthOptions, setBandwidthOptions] =
    useState<string[]>(bandwidthOption);
  const dimension = useUpdateDimension(chartId);

  const { stackedSeries, totalBandWidth } = useMemo(() => {
    const grouped = getBandwidthMetrics(data);
    const stack = d3.stack<BandWidthMetrics, "request" | "response">();

    const bandwidthIntersection = Array.from(
      d3.intersection(bandwidthOption, bandwidthOptions)
    ) as ("request" | "response")[];

    const stackedSeries = stack.keys(bandwidthIntersection)(grouped);
    const totalBandWidth = grouped.reduce((sum, next) => {
      const bandwidthValue =
        bandwidthOptions.length === 2
          ? next.totalBandWidth || 0
          : next[bandwidthOptions[0]] || 0;
      return sum + bandwidthValue;
    }, 0);

    return { stackedSeries, totalBandWidth };
  }, [bandwidthOptions, data]);

  const { xDomain, yDomain } = useMemo(
    () => getDomains(stackedSeries, timeFrame),
    [timeFrame, stackedSeries]
  );

  const { xScale, yScale, positions } = getLinearAxis({
    dimension: dimension,
    xDomain,
    yDomain
  });

  const totalBandwidth = autoFormatBytes(totalBandWidth);
  const disable =
    stackedSeries?.[0]?.length == 0 && stackedSeries?.[1]?.length == 0;
  const xtick = getXTickCount(dimension.width, timeFrame, 17);

  const cursorFuncProps = useMemo(
    () => ({
      xScale,
      yScale,
      data,
      bisectFn: d => d.date,
      disable,
      chartId
    }),
    [data, disable, xScale, yScale]
  );

  const { cursorSync, handleCursorMove } = useCursorDataSync({
    cursorFuncProps
  });
  const adaptedCursor = cursorSync?.adaptedCursor;

  return (
    <>
      <S.ChartTitleContainer>
        <S.ChartTitleList>
          <S.ChartTitle tag="h3">
            {intl.formatMessage({ id: "bandwidth" })}
          </S.ChartTitle>
          <ChartInfo
            title={intl.formatMessage({ id: "total_bandwidth" })}
            description={intl.formatMessage({ id: "http.bandwidth.info" })}
          />
          <BandwidthOptionWrapper>
            <BandwidthOptionFilter
              setBandwidthOption={setBandwidthOptions}
              bandwidthOptions={bandwidthOptions}
            />
          </BandwidthOptionWrapper>
        </S.ChartTitleList>
        <S.ChartTitle className="sum" tag="h3">
          <Tooltip
            align="end"
            tooltip={`${totalBandwidth} total from ${formatTimeFrame(
              timeFrame
            )}`}
            analyticId="total_bandwidth"
          >
            {totalBandwidth}
          </Tooltip>
        </S.ChartTitle>
      </S.ChartTitleContainer>
      <HttpLayout
        tick={{
          y: {
            format: (bytes: number) => autoFormatBytes(bytes)
          },
          x: xtick
        }}
        chartId={chartId}
        yScale={yScale}
        xScale={xScale}
        positions={positions}
        onMouseMove={handleCursorMove}
        onMouseLeave={() => handleCursorMove?.(null)}
        cursorData={adaptedCursor}
      >
        {disable ? (
          <NoData />
        ) : (
          stackedSeries.map(data => {
            return (
              <Area
                chartId={chartId}
                key={data.key}
                xScale={xScale}
                yScale={yScale}
                data={data}
              />
            );
          })
        )}
      </HttpLayout>
      {adaptedCursor?.isInCursorGroup && (
        <TooltipContent cursorSync={cursorSync} series={stackedSeries} />
      )}
    </>
  );
};
export default HttpBandWidth;
